/**
 * AuthService - Handles authentication, token management, and currency storage
 */

import { getCurrencyConfig, DEFAULT_CURRENCY } from "../config/currencyConfig";

interface DecodedToken {
  exp: number;
  iat?: number;
  encryptedToken?: string;
  [key: string]: any;
}

class AuthServiceClass {
  private static TOKEN_KEY = "token";
  private static EVENT_ID_KEY = "eventId";
  private static CURRENCY_KEY = "currency";
  private static DEFAULT_EVENT_ID = "event-1"; // Aviator game event ID (event-6 is Plinko)

  /**
   * Decode JWT token without verification (client-side only)
   */
  decodeToken(token: string): DecodedToken | null {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        return null;
      }

      const payload = parts[1];
      const decoded = JSON.parse(atob(payload));
      return decoded;
    } catch {
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;

    const now = Date.now() / 1000;
    return decoded.exp <= now;
  }

  /**
   * Store token in localStorage
   */
  setToken(token: string): void {
    const normalizedToken = token.trim().replace(/^Bearer\s+/i, "");
    localStorage.setItem(AuthServiceClass.TOKEN_KEY, normalizedToken);
  }

  /**
   * Get token from localStorage
   */
  getToken(): string | null {
    const token = localStorage.getItem(AuthServiceClass.TOKEN_KEY);
    return token?.trim().replace(/^Bearer\s+/i, "") || null;
  }

  /**
   * Remove token from localStorage
   */
  removeToken(): void {
    localStorage.removeItem(AuthServiceClass.TOKEN_KEY);
  }

  /**
   * Store event ID in localStorage
   */
  setEventId(eventId: string): void {
    localStorage.setItem(AuthServiceClass.EVENT_ID_KEY, eventId);
  }

  /**
   * Get event ID from localStorage (defaults to event-1 for Aviator)
   */
  getEventId(): string {
    return localStorage.getItem(AuthServiceClass.EVENT_ID_KEY) || AuthServiceClass.DEFAULT_EVENT_ID;
  }

  /**
   * Remove event ID from localStorage
   */
  removeEventId(): void {
    localStorage.removeItem(AuthServiceClass.EVENT_ID_KEY);
  }

  /**
   * Store currency in localStorage
   */
  setCurrency(currency: string): void {
    const upperCurrency = currency?.trim().toUpperCase() || DEFAULT_CURRENCY;
    localStorage.setItem(AuthServiceClass.CURRENCY_KEY, upperCurrency);
  }

  /**
   * Get currency from localStorage
   */
  getCurrency(): string {
    return localStorage.getItem(AuthServiceClass.CURRENCY_KEY) || DEFAULT_CURRENCY;
  }

  /**
   * Get currency config for current currency
   */
  getCurrencyConfig() {
    return getCurrencyConfig(this.getCurrency());
  }

  /**
   * Remove currency from localStorage
   */
  removeCurrency(): void {
    localStorage.removeItem(AuthServiceClass.CURRENCY_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return Boolean(this.getToken());
  }

  /**
   * Clear all auth data
   */
  clearAll(): void {
    this.removeToken();
    this.removeEventId();
    this.removeCurrency();
  }

  /**
   * Initialize auth from URL (used by AuthWrapper)
   * Returns false if no valid auth found
   */
  initializeFromUrl(pathname: string, search: string): {
    success: boolean;
    token?: string;
    eventId?: string;
    currency?: string;
    error?: string;
  } {
    // PlayFortune Aviator launch contract:
    // /auth/:eventId/:token/:currency
    // The query-string currency form remains supported for older launchers.
    const pathSegments = pathname.split("/").filter(Boolean);
    const authIndex = pathSegments.findIndex(
      (segment) => segment.toLowerCase() === "auth",
    );
    const eventIdFromPath =
      authIndex >= 0 ? pathSegments[authIndex + 1] : undefined;
    const tokenFromPath =
      authIndex >= 0 ? pathSegments[authIndex + 2] : undefined;
    const currencySegment =
      authIndex >= 0 ? pathSegments[authIndex + 3]?.trim() : undefined;

    let eventId: string | null = null;
    let token: string | null = null;
    let currencyFromPath = "";
    try {
      eventId = eventIdFromPath
        ? decodeURIComponent(eventIdFromPath).trim()
        : null;
      token = tokenFromPath
        ? decodeURIComponent(tokenFromPath).trim().replace(/^Bearer\s+/i, "")
        : null;
      currencyFromPath = currencySegment
        ? decodeURIComponent(currencySegment).replace(/^currency=/i, "").trim()
        : "";
    } catch {
      return { success: false, error: "Invalid launch URL encoding" };
    }

    // Extract currency from query params or path, default to USD
    const searchParams = new URLSearchParams(search);
    const currencyFromQuery = searchParams.get("currency");
    const currency = currencyFromPath || currencyFromQuery || DEFAULT_CURRENCY;

    if (!eventId || !token) {
      return {
        success: false,
        error: "Launch URL must include event ID and token",
      };
    }

    return {
      success: true,
      token,
      eventId,
      currency: currency.toUpperCase(),
    };
  }
}

// Export singleton instance
export const AuthService = new AuthServiceClass();
